/**
 * attr-value-rules — the VALUE-TYPE half of React's attribute write rules.
 *
 * BUG-RUNTIME-128. The precedence ATLAS-DOMWRITE-012 x ATLAS-DOMWRITE-013
 * declares: where a BOOLEAN value meets an attribute that keeps a plain string
 * verbatim, the VALUE-TYPE rule governs and the attribute-kind rule does not
 * apply. React tests the value first and the attribute kind second, so a
 * boolean never reaches the verbatim-string path at all - `title={true}` emits
 * NO attribute, not `title="true"` and not `title=""`.
 *
 * That rule had only ever been implemented for the single attribute each bug
 * was reported on (`className` on compiled, BUG-COMPILER-006; `class` on
 * compat, BUG-RUNTIME-119), which is a precedence error committed at fix time:
 * every other verbatim-string attribute kept the wrong reading. This module is
 * the rule itself, so every writer on both paths asks ONE question and a
 * later table move cannot make two writers disagree (the explicit warning in
 * BUG-RUNTIME-130: a fix that duplicates a table rather than sharing it will
 * diverge again).
 *
 * Ground truth is react-dom 19.2.4's `setProp` switch, read from the pinned
 * oracle at testing/atlas/oracle, and corroborated by the boolean column of
 * docs/atlas/data/attribute-behavior.json (React's own
 * `fixtures/attribute-behavior` matrix, re-measured):
 *
 *   - one group takes `setAttribute(key, '')` when truthy and
 *     `removeAttribute(key)` otherwise (`disabled`, `hidden`, `required`, …);
 *   - one group takes `setAttribute(key, '' + value)`, so a boolean lands as
 *     the STRING "true"/"false" (`contentEditable`, `draggable`, `spellCheck`
 *     and the SVG enumerated names);
 *   - `aria-*` / `data-*` also serialize verbatim (ATLAS-DOMWRITE-004);
 *   - EVERYTHING ELSE reaches `setValueForAttribute`, whose `case 'boolean'`
 *     calls `removeAttribute` for any name not prefixed `data-`/`aria-`. That
 *     is 501 of the 536 rows in the matrix.
 *
 * Scope notes, deliberately:
 *   - `value` is in React's string-serializing group, but every writer here
 *     routes `value`/`defaultValue`/`checked`/`defaultChecked` through the
 *     form-control property paths long before the generic arm
 *     (ATLAS-DOMWRITE-010), so it is NOT listed - adding it would divert a
 *     controlled input into a plain setAttribute.
 *   - BOOLEAN_EMPTY_STRING_PROPS is the list the compiled and compat writers
 *     already carried, unchanged: this module was extracted to fix the
 *     FALL-THROUGH, not to re-litigate membership. `autoComplete`,
 *     `autoFocus`, `capture`, `download` and `selected` are ours rather than
 *     React's; each has its own handling upstream of the generic arm on at
 *     least one path, and moving them is a separate measurement.
 */
/**
 * Boolean props whose truthy form is the EMPTY STRING attribute and whose
 * falsy form removes it. Presence alone is what these mean in HTML, so
 * `disabled="false"` would disable the element (BUG-RUNTIME-006).
 */
export const BOOLEAN_EMPTY_STRING_PROPS = new Set([
    'disabled', 'checked', 'readOnly', 'required', 'hidden', 'multiple',
    'selected', 'autoFocus', 'autoPlay', 'controls', 'default', 'defer',
    'formNoValidate', 'noValidate', 'noModule', 'open', 'reversed', 'scoped',
    'seamless', 'allowFullScreen', 'async', 'autoComplete', 'capture',
    'download', 'itemScope', 'loop', 'muted', 'playsInline', 'inert',
]);
/**
 * Props React serializes with `'' + value`, so a boolean reads back as the
 * literal string "true" / "false". React 19.2.4 `setProp`:
 * contentEditable, spellCheck, draggable, value, autoReverse,
 * externalResourcesRequired, focusable, preserveAlpha. (`value` excluded -
 * see the scope note above.)
 */
export const STRING_BOOLEAN_PROPS = new Set([
    'contentEditable', 'spellCheck', 'draggable',
    'autoReverse', 'externalResourcesRequired', 'focusable', 'preserveAlpha',
]);
/**
 * What a BOOLEAN prop value does on this attribute name:
 *   'empty'  → setAttribute(name, '') when true, removeAttribute when false
 *   'string' → setAttribute(name, String(value)) either way
 *   'drop'   → no attribute at all, for either value
 *
 * Callers own the write; this owns the decision.
 */
export function booleanPropWrite(key) {
    if (BOOLEAN_EMPTY_STRING_PROPS.has(key))
        return 'empty';
    if (STRING_BOOLEAN_PROPS.has(key))
        return 'string';
    // ATLAS-DOMWRITE-004: React's setValueForAttribute exempts exactly these two
    // prefixes from the boolean drop and writes `'' + value`.
    if (key.startsWith('aria-') || key.startsWith('data-'))
        return 'string';
    return 'drop';
}
/* -------------------------------------------------------------------------
 * The URL half: React's `javascript:` sanitizer.
 *
 * BUG-RUNTIME-142 / CLAIM-SRC-0013. React 19 does not write an author's
 * `javascript:` URL to a navigable attribute. `sanitizeURL`
 * (react-dom-bindings/src/shared/sanitizeURL.js) replaces the whole value with
 * a stub that throws if anything ever navigates to it. Both JetShake paths
 * wrote the payload through untouched, which made our output MORE dangerous
 * than React's while every DOM-parity gate read green - the attribute is
 * present in both and only its VALUE differs.
 *
 * It lives here, beside the value-type rule, for the reason that rule records:
 * the population is what matters, and fixing the single attribute a bug was
 * reported on is a precedence error committed at fix time. The probe that
 * found this measured `form action` only; React applies the sanitizer to a
 * LIST, read from the pinned oracle's `setProp` switch (react-dom 19.2.4):
 *
 *   case 'src' / 'href'          -> setAttribute(key, sanitizeURL('' + v))
 *   case 'action' / 'formAction' -> setAttribute(key, sanitizeURL('' + v))
 *   case 'xlinkHref'             -> setAttributeNS(xlink, 'xlink:href',
 *                                                  sanitizeURL('' + v))
 *
 * and nowhere else. Notably NOT `srcSet`, `data`, `poster`, `cite`,
 * `formTarget`, or any `data-`/`aria-` name - React leaves those verbatim, so
 * widening this set would be a divergence in the other direction.
 *
 * Ordering, which is observable: the sanitizer runs on the STRINGIFIED value
 * and AFTER the nullish / boolean / function / symbol removals in React's own
 * arms, so it can never resurrect an attribute those rules dropped. Callers
 * must ask it for the string they are about to write, immediately before
 * writing it.
 *
 * The `action` / `formAction` FUNCTION form is a different rule with a
 * different stub (React's "A React form was unexpectedly submitted" URL) and is
 * handled by the form-action writers upstream; a function never reaches here.
 * ---------------------------------------------------------------------- */
/**
 * Prop names React runs through `sanitizeURL`. Keyed on the PROP name, not the
 * attribute name, exactly as React's switch is: `xlinkHref` is sanitized while
 * every other `xlink*` prop is not.
 */
export const URL_SANITIZED_PROPS = new Set([
    'src', 'href', 'action', 'formAction', 'xlinkHref',
]);
/**
 * The exact replacement React writes. Byte-for-byte from react-dom 19.2.4 -
 * apps and tests match on this string, so it is a contract, not a message.
 */
export const BLOCKED_JAVASCRIPT_URL = "javascript:throw new Error('React has blocked a javascript: URL as a security precaution.')";
/**
 * React's `isJavaScriptProtocol`, verbatim. The interleaved `[\r\n\t]*` and the
 * leading C0-plus-space class are not decoration: HTML attribute parsing strips
 * those characters, so `java&#9;script:` and a leading NUL both navigate. A
 * naive `startsWith('javascript:')` is bypassed by either, which is why this
 * regex is COPIED from the oracle rather than re-derived.
 */
const IS_JAVASCRIPT_PROTOCOL = /^[\u0000-\u001F ]*j[\r\n\t]*a[\r\n\t]*v[\r\n\t]*a[\r\n\t]*s[\r\n\t]*c[\r\n\t]*r[\r\n\t]*i[\r\n\t]*p[\r\n\t]*t[\r\n\t]*:/i;
/**
 * The protocol test alone, split out from `sanitizeUrlProp` for the COMPILED
 * path (BUG-RUNTIME-153).
 *
 * A runtime writer is handed an arbitrary prop name and has to ask both
 * questions - is this one of the five, and is this value a `javascript:` URL.
 * Compiled output does not: the compiler knows the prop name at build time and
 * has already answered the first question from its own mirror of
 * `URL_SANITIZED_PROPS`, so the emitted call only needs the second. Splitting
 * it keeps the emitted call site to `__su(String(v))` - no prop-name string
 * argument in the bundle, and no set membership test at runtime - while the
 * regex and the stub string stay in ONE place, which is the whole point of this
 * module (BUG-RUNTIME-130's standing lesson).
 *
 * `src/runtime/compiled/url-sanitize.ts` re-exports this as `_su` so a compiled
 * graph reaches it without importing the boolean tables above.
 */
export function blockJavaScriptUrl(value) {
    return IS_JAVASCRIPT_PROTOCOL.test(value) ? BLOCKED_JAVASCRIPT_URL : value;
}
/**
 * What to WRITE for `key`, given the already-stringified `value`. Returns
 * `value` unchanged for every prop outside the list and every URL that is not a
 * `javascript:` one, so a caller can use it unconditionally.
 *
 * Callers own the write; this owns the decision (same contract as
 * `booleanPropWrite`).
 */
export function sanitizeUrlProp(key, value) {
    if (!URL_SANITIZED_PROPS.has(key))
        return value;
    return blockJavaScriptUrl(value);
}
